import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", type: "customer", tax_number: "", phone: "", email: "", notes: "" };

export default function Contacts() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/contacts"); setItems(data); };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((i) => filter === "all" ? true : i.type === filter);

  const save = async () => {
    if (!form.name) { toast.error("İsim zorunlu"); return; }
    try {
      if (editing) await api.put(`/contacts/${editing.id}`, form);
      else await api.post("/contacts", form);
      toast.success(editing ? "Güncellendi" : "Eklendi");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Silmek istiyor musunuz?")) return;
    await api.delete(`/contacts/${id}`); toast.success("Silindi"); await load();
  };
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, type: c.type, tax_number: c.tax_number || "", phone: c.phone || "", email: c.email || "", notes: c.notes || "" }); setOpen(true); };

  return (
    <div className="space-y-6 slide-up" data-testid="contacts-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-indigo-400 mb-2">Cari Hesaplar</div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Müşteri & Tedarikçiler</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">İş ortaklarınızla cari hareketlerinizi takip edin.</p>
        </div>
        <div className="flex gap-3">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="bg-[#131D31] border border-[#1E293B]">
              <TabsTrigger value="all">Tümü</TabsTrigger>
              <TabsTrigger value="customer">Müşteri</TabsTrigger>
              <TabsTrigger value="supplier">Tedarikçi</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button data-testid="new-contact-btn" onClick={openNew} className="bg-indigo-500 hover:bg-indigo-600"><Plus className="w-4 h-4 mr-2"/>Yeni Cari</Button>
        </div>
      </div>

      <div className="card-elev overflow-hidden" data-testid="contacts-list">
        <table className="w-full text-sm">
          <thead className="bg-[#0F172A] text-[#94A3B8]">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Ad</th>
              <th className="text-left px-5 py-3 font-medium">Tür</th>
              <th className="text-left px-5 py-3 font-medium">Vergi No</th>
              <th className="text-left px-5 py-3 font-medium">Telefon</th>
              <th className="text-left px-5 py-3 font-medium">E-posta</th>
              <th className="text-right px-5 py-3 font-medium">Bakiye (TRY)</th>
              <th className="text-right px-5 py-3 font-medium">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-[#94A3B8]"><Users className="w-10 h-10 mx-auto mb-2 text-[#1E293B]"/>Henüz cari hesap yok.</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-[#1E293B] hover:bg-[#0F172A]/50" data-testid={`contact-row-${c.id}`}>
                <td className="px-5 py-3 font-medium">{c.name}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full border ${c.type === "customer" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-indigo-500/30 bg-indigo-500/10 text-indigo-400"}`}>
                    {c.type === "customer" ? "Müşteri" : "Tedarikçi"}
                  </span>
                </td>
                <td className="px-5 py-3 text-[#94A3B8] mono">{c.tax_number || "—"}</td>
                <td className="px-5 py-3 text-[#94A3B8]">{c.phone || "—"}</td>
                <td className="px-5 py-3 text-[#94A3B8]">{c.email || "—"}</td>
                <td className={`px-5 py-3 text-right mono font-semibold ${(c.balance_try || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>{formatMoney(c.balance_try || 0, "TRY")}</td>
                <td className="px-5 py-3 text-right">
                  <button data-testid={`edit-contact-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-indigo-400 mr-1"><Pencil className="w-3.5 h-3.5"/></button>
                  <button data-testid={`delete-contact-${c.id}`} onClick={() => remove(c.id)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-red-400"><Trash2 className="w-3.5 h-3.5"/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131D31] border-[#1E293B] text-white">
          <DialogHeader><DialogTitle>{editing ? "Cariyi Düzenle" : "Yeni Cari Hesap"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#94A3B8]">Ad / Unvan</Label>
              <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0F172A] border-[#1E293B] mt-1" />
            </div>
            <div>
              <Label className="text-xs text-[#94A3B8]">Tür</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="contact-type" className="bg-[#0F172A] border-[#1E293B] mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#131D31] border-[#1E293B]">
                  <SelectItem value="customer">Müşteri</SelectItem>
                  <SelectItem value="supplier">Tedarikçi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#94A3B8]">Vergi No</Label>
                <Input value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} className="bg-[#0F172A] border-[#1E293B] mt-1" />
              </div>
              <div>
                <Label className="text-xs text-[#94A3B8]">Telefon</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0F172A] border-[#1E293B] mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#94A3B8]">E-posta</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0F172A] border-[#1E293B] mt-1" />
            </div>
            <div>
              <Label className="text-xs text-[#94A3B8]">Not</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0F172A] border-[#1E293B] mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} data-testid="contact-save-btn" className="bg-indigo-500 hover:bg-indigo-600">{editing ? "Güncelle" : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
